import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useGame } from "../header/GameContext";
import { saveToSheet } from "../../api/sheet";
import {
  createSurvey,
  updateSurvey,
  clearSurveyStorage,
  getSurveyId,
} from "../../api/surveyApi";

import { SURVEY_DATA } from "./surveyData";

import ButtonOnly from "../button/buttonOnly";

import "./survey.css";

const TOTAL = SURVEY_DATA.length;
const STORAGE_KEY = "surveyAnswers";

// ─── 로컬스토리지 헬퍼
const loadAnswers = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY)) ?? {};
  } catch {
    return {};
  }
};

const persistAnswer = (step, value) => {
  const prev = loadAnswers();
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prev, [step]: value }));
};

export default function Survey() {
  const navigate = useNavigate();

  // ✅ useParams로 URL의 :step 파라미터 추출
  const { step: stepParam } = useParams();
  const step = Number(stepParam) || 1;

  const { pauseTimer, playerKeyCode } = useGame();

  const userId = localStorage.getItem("gameUserId");

  // 타이머 멈춤
  useEffect(() => {
    pauseTimer();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const idx = step - 1;
  const question = SURVEY_DATA[idx];
  const isLast = step === TOTAL;

  const [answer, setAnswer] = useState(
    () => loadAnswers()[step] ?? (question?.type === "multi" ? [] : ""),
  );
  const [submitting, setSubmitting] = useState(false);

  // step 변경 시 해당 step의 저장된 답변 복원
  useEffect(() => {
    const saved = loadAnswers()[step];
    setAnswer(saved ?? (question?.type === "multi" ? [] : ""));
  }, [step]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!question) return null;

  // ── 답변 선택 핸들러
  const handleSelect = (opt) => {
    if (question.type === "single") {
      setAnswer(opt);
      persistAnswer(step, opt);
    } else if (question.type === "multi") {
      const next = answer.includes(opt)
        ? answer.filter((v) => v !== opt)
        : [...answer, opt];
      setAnswer(next);
      persistAnswer(next, step); // ✅ 인자 순서 수정: (step, value)
      persistAnswer(step, next);
    }
  };

  const handleTextChange = (e) => {
    setAnswer(e.target.value);
    persistAnswer(step, e.target.value);
  };

  // ── 단계별 DB + 시트 저장
  const saveStepToDB = async () => {
    const colKey = `q${step}`;
    const colValue = loadAnswers()[step] ?? answer;

    // DB 저장
    if (!getSurveyId()) {
      await createSurvey(userId, colKey, colValue);
    } else {
      await updateSurvey(colKey, colValue);
    }

    // 구글 시트 저장 (단계별 누적)
    const raw = loadAnswers();
    const mapped = Object.fromEntries(
      Object.entries(raw).map(([k, v]) => [
        `survey${k}`,
        Array.isArray(v) ? JSON.stringify(v) : v,
      ]),
    );

    try {
      await saveToSheet({ keyCode: playerKeyCode, ...mapped });
    } catch (e) {
      console.error(`[Sheet] step ${step} 저장 실패`, e.message);
    }
  };

  // ── 최종 제출
  const handleFinalSubmit = async () => {
    setSubmitting(true);
    localStorage.removeItem(STORAGE_KEY);
    clearSurveyStorage();
    setSubmitting(false);
    navigate("/ranking");
  };

  // ── 다음으로 / 제출
  const handleConfirm = async () => {
    setSubmitting(true);

    try {
      await saveStepToDB();
    } catch (e) {
      console.error(`[Survey] step ${step} 저장 실패`, e);
    }

    if (!isLast) {
      setSubmitting(false);
      // ✅ 다음 step URL로 이동
      navigate(`/survey/${step + 1}`);
      return;
    }

    await handleFinalSubmit();
    setSubmitting(false);
  };

  // ── 다음 버튼 활성화 조건
  const canProceed =
    question.type === "text"
      ? true
      : question.type === "multi"
        ? answer.length > 0
        : !!answer;

  return (
    <div id="content">
      <div className="box content_box1 ans0001 surveyBox">
        <div className="box_con con_box1 con_pd5 con_box_c">
          <div className="mission_tit surveyTit"></div>

          <div className="survey_wrap">
            <div className="survey_top mt48">
              <p className="survey_t1">[{question.num}]</p>
              <p
                className="survey_t2"
                dangerouslySetInnerHTML={{ __html: question.text }}
              ></p>
            </div>

            <div className="survey_answer">
              {/* 선택형 (single / multi) */}
              {(question.type === "single" || question.type === "multi") && (
                <ul>
                  {question.options.map((opt) => {
                    const active =
                      question.type === "multi"
                        ? answer.includes(opt)
                        : answer === opt;
                    return (
                      <li key={opt}>
                        <button
                          className={`survey_answer_btn${active ? " active" : ""}`}
                          onClick={() => handleSelect(opt)}
                        >
                          {opt}
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* 서술형 */}
              {question.type === "text" && (
                <textarea
                  className="survey_textarea"
                  value={answer}
                  onChange={handleTextChange}
                  placeholder="내용을 입력하세요."
                  rows={6}
                />
              )}

              {/* 다음 / 제출 버튼 */}
              <ButtonOnly
                btnValue={
                  submitting ? (
                    <span className="btn-spinner" />
                  ) : isLast ? (
                    "제출하고 랭킹 확인하기"
                  ) : (
                    "다음으로"
                  )
                }
                btnClass={`surveySubmitBtn c2 mt24${
                  !canProceed || submitting ? " disabled" : " active"
                }`}
                onFinish={handleConfirm}
                disabled={!canProceed || submitting}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
